import { Box, Button, Flex, FormControl, FormErrorMessage, FormLabel, Input, Select } from '@chakra-ui/react';
import { US_STATES } from '@common/constants';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import { zipCodeRegExUs } from '@common/utility/validations';
import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { useAccountInfoViewModel } from '../models/InfoViewModel';
import { InfoForm } from '../types';

type AddressFormProps = {
  infoForm: UseFormReturn<InfoForm>;
};

export const AddressForm: React.FC<AddressFormProps> = ({ infoForm }) => {
  const [showAddressForm, setShowAddressForm] = useState(false);
  const viewModel = useAccountInfoViewModel();
  const { investorAccount } = useInvestorAccountModel();
  const errors = infoForm.formState.errors;
  return (
    <Box>
      <Flex justifyContent={'space-between'}>
        <TextBlock color={'gray.600'}>Address</TextBlock>
        <Button
          onClick={() => {
            infoForm.setValue('address1', investorAccount?.address || '');
            infoForm.setValue('address2', investorAccount?.address2 || '');
            infoForm.setValue('city', investorAccount?.city || '');
            infoForm.setValue('state', investorAccount?.state || '');
            infoForm.setValue('zip', investorAccount?.zip || '');
            setShowAddressForm(!showAddressForm);
          }}
          variant="link">
          {showAddressForm ? 'Cancel' : 'Edit'}
        </Button>
      </Flex>

      {showAddressForm ? (
        <>
          <FormControl isInvalid={Boolean(errors.address1)}>
            <FormLabel htmlFor="address1">Address 1</FormLabel>
            <Input
              id="address1"
              mb={3}
              placeholder="Address 1"
              {...infoForm.register('address1', {
                required: 'Please enter a street address',
              })}
              defaultValue={investorAccount?.address || ''}
            />
            <FormErrorMessage>{errors.address1 && errors.address1.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={Boolean(errors.address2)}>
            <FormLabel htmlFor="address2">Address 2</FormLabel>
            <Input
              id="address2"
              mb={3}
              {...infoForm.register('address2', {})}
              defaultValue={investorAccount?.address2 || ''}
            />
          </FormControl>
          <FormControl isInvalid={Boolean(errors.city)}>
            <FormLabel htmlFor="city">City</FormLabel>
            <Input
              id="city"
              mb={3}
              placeholder="City"
              {...infoForm.register('city', {
                required: 'Please enter a city',
              })}
              defaultValue={investorAccount?.city || ''}
            />
            <FormErrorMessage>{errors.city && errors.city.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={Boolean(errors.state)}>
            <FormLabel htmlFor="state">State</FormLabel>
            <Select
              mb={3}
              placeholder="Select option"
              defaultValue={viewModel.state || ''}
              {...infoForm.register('state', {
                required: 'Please enter a state',
              })}>
              {Object.keys(US_STATES).map((key) => {
                return (
                  <option key={`state-${key}`} value={key}>
                    {US_STATES[key]}
                  </option>
                );
              })}
            </Select>
            <FormErrorMessage>{errors.state && errors.state.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={Boolean(errors.zip)}>
            <FormLabel htmlFor="zip">Zip Code</FormLabel>
            <Input
              id="zip"
              mb={3}
              placeholder="Zip Code"
              {...infoForm.register('zip', {
                pattern: {
                  message: 'Please use a valid zip code',
                  value: zipCodeRegExUs,
                },
                required: 'Please enter a zip code',
              })}
              defaultValue={investorAccount?.zip || ''}
            />
            <FormErrorMessage>{errors?.zip?.message}</FormErrorMessage>
          </FormControl>
          <Flex justifyContent={'flex-end'}>
            <Button
              onClick={async () => {
                viewModel.setState('LOADING');
                const { address1, address2, city, state, zip } = infoForm.getValues();
                const status = await viewModel.updateAddress({ address1, address2, city, state, zip });
                if (status.success) {
                  window.location.reload();
                } else {
                  viewModel.setState('RENDER');
                }
              }}>
              Update
            </Button>
          </Flex>
        </>
      ) : (
        <>
          <TextBlock weight="bold">{investorAccount?.address}</TextBlock>
          {investorAccount?.address2 ? <TextBlock weight="bold">{investorAccount?.address2}</TextBlock> : null}
          <TextBlock weight="bold">{`${investorAccount?.city || ''} ${
            investorAccount?.state ? `, ${investorAccount.state}` : ''
          }`}</TextBlock>
          <TextBlock weight="bold">{investorAccount?.zip}</TextBlock>
        </>
      )}
    </Box>
  );
};
